// Review paragraph deletion
// --------------------------
// Single, shared builder for the Firestore write that removes a reviewable
// paragraph from a report during AI-output review.
//
// Why this exists (parity, not novelty):
// The ADHD SSI Strengths flow (RD_SSI.js) already lets a clinician delete a
// generated paragraph. It does so by clearing the output to "" (leveraging the
// existing empty-string filters that hide a paragraph everywhere it is read),
// deleteField()-ing its approval record, and appending an { isDeleted: true }
// audit entry to editHistory. The Autism OBS Self-Report delete needs exactly
// the same write. Rather than hand-roll a second copy (and risk the two drifting
// apart), the write shape lives here as one pure function, following the same
// one-file/multi-variant convention as sectionReviewFinalization.js.
//
// It is PURE (no Firestore I/O, no reads of auth/db) so it can be unit-tested by
// asserting the returned dot-notation payload directly. Callers pass it straight
// to updateDoc(docRef, payload).
//
// The empty-string sentinel (not deleteField() on outputs) is deliberate and
// load-bearing: across the codebase, "a paragraph with empty output is not in
// the report" is the single rule that computeValidCodes, the render-side
// approval gate, and the backend template builder all already honour. Recording
// isDeleted: true in editHistory is what distinguishes a deliberate deletion
// from a paragraph that was simply never generated (both look empty in outputs).

import { deleteField, serverTimestamp } from 'firebase/firestore';

/**
 * Build the flat Firestore update payload that removes a reviewable paragraph
 * from a report.
 *
 * @param {Object} params
 * @param {string} [params.pathPrefix='']  Dot-notation prefix for the fields.
 *   - Autism: '' — outputs / approvedParagraphs / editHistory are root fields on
 *     the reportContent/{stageKey} subcollection document.
 *   - ADHD:   'dataModel.6_manageReports.sections.{N}.' — the same maps nested
 *     under the monolithic patient document. MUST end with a trailing dot.
 * @param {string} params.code            Paragraph code to delete (e.g. 'asd_obs_self_report').
 * @param {string} [params.previousValue] Prior output text, stored in the audit entry for rollback/history.
 * @param {string} params.clinicianId     Acting clinician uid (auth.currentUser.uid).
 * @param {string} params.clinicianName   Acting clinician display name.
 * @param {string[]} [params.alsoClearCodes=[]]  Additional OUTPUT keys to clear to ''
 *   alongside the paragraph (ADHD strengths clear their paired title code; Autism
 *   Self-Report has none, so this is empty).
 * @param {string} [params.timestampKey]  editHistory sub-key; defaults to Date.now().
 * @returns {Object} Dot-notation update payload for updateDoc.
 */
export function buildParagraphDeletionWrites({
  pathPrefix = '',
  code,
  previousValue,
  clinicianId,
  clinicianName,
  alsoClearCodes = [],
  timestampKey = Date.now().toString(),
}) {
  if (!code) {
    throw new Error('buildParagraphDeletionWrites requires a paragraph code');
  }

  const payload = {
    // Clear the paragraph output. Empty string (not deleteField) so every
    // empty-string filter keeps it hidden without changing the document shape.
    [`${pathPrefix}outputs.${code}`]: '',
    // A deleted paragraph must not remain approved.
    [`${pathPrefix}approvedParagraphs.${code}`]: deleteField(),
    // Audit trail: isDeleted distinguishes a deliberate deletion from an
    // ungenerated paragraph, since both read as empty in outputs.
    [`${pathPrefix}editHistory.${code}.${timestampKey}`]: {
      clinicianId,
      clinicianName,
      timestamp: serverTimestamp(),
      previousValue,
      newValue: '',
      isDeleted: true,
    },
    // Review metadata (mirrors the approval / edit writes).
    [`${pathPrefix}lastReviewedAt`]: serverTimestamp(),
    [`${pathPrefix}reviewedBy`]: clinicianId,
    [`${pathPrefix}reviewedByName`]: clinicianName,
  };

  // Paired metadata outputs (e.g. an ADHD strength's title code) are cleared to
  // '' the same way, so they disappear from the report with the paragraph.
  alsoClearCodes.forEach((clearCode) => {
    payload[`${pathPrefix}outputs.${clearCode}`] = '';
  });

  return payload;
}
